import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Chip,
  alpha,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Group, ChatBubble, Circle } from '@mui/icons-material';
import { communityApi } from '../../../services/communityApi';
import { CardGridSkeleton } from '../../skeletons';

interface CommunitiesTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  navigate: any;
  user: any;
  lokiGradient: string;
  goldColor: string;
  deepGreen: string;
  lightGreen: string;
}

const CommunitiesTab: React.FC<CommunitiesTabProps> = ({
  isDark,
  colors,
  discordFont,
  glassStyle,
  navigate,
  user,
  lokiGradient,
  goldColor,
  deepGreen,
  lightGreen,
}) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [userCommunities, setUserCommunities] = useState<any[]>([]);
  const [communitiesLoading, setCommunitiesLoading] = useState(false);

  const fetchUserCommunities = async () => {
    if (!user) return;
    
    try {
      setCommunitiesLoading(true);
      const response = await communityApi.getUserCommunities({
        page: 1,
        limit: 20
      });
      
      if (response.success && response.communities) {
        const safeCommunities = response.communities.map((community: any) => ({
          id: community.id,
          name: community.name,
          slug: community.slug,
          description: community.description,
          category: community.category,
          profilePicture: community.profilePicture,
          bannerImage: community.bannerImage,
          membersCount: community.membersCount || 0,
          postsCount: community.postsCount || 0,
          owner: community.owner || { id: '', username: 'Unknown' },
          role: community.role || 'member'
        }));
        setUserCommunities(safeCommunities);
      }
    } catch (error) {
      console.error('Failed to fetch user communities:', error);
    } finally {
      setCommunitiesLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserCommunities();
    }
  }, [user]);

  return (
    <Box>
      <Typography variant="h5" sx={{ 
        mb: 1, 
        ...discordFont, 
        color: colors.textPrimary,
        fontSize: isMobile ? '1.5rem' : '2rem'
      }}>
        My Communities
      </Typography>
      <Typography color={colors.textSecondary} sx={{ mb: 4 }}>
        Communities you are a part of. Click on a community to visit its page.
      </Typography>
      
      {communitiesLoading ? (
        <CardGridSkeleton count={6} size={{ xs: 12, sm: 6, md: 4 }} label="Loading your communities" />
      ) : userCommunities.length === 0 ? (
        <Paper elevation={0} sx={{ 
          bgcolor: colors.cardBg, 
          borderRadius: isMobile ? '16px' : '24px', 
          p: isMobile ? 4 : 6, 
          textAlign: 'center',
          ...glassStyle 
        }}>
          <Group sx={{ 
            fontSize: isMobile ? 40 : 60, 
            color: colors.accent, 
            mb: 2, 
            opacity: 0.5 
          }} />
          <Typography variant="h6" sx={{ 
            mb: 2, 
            color: colors.textPrimary, 
            fontWeight: 700,
            fontSize: isMobile ? '1.25rem' : '1.5rem'
          }}>
            No Communities Yet
          </Typography>
          <Typography color={colors.textSecondary} sx={{ 
            mb: 4, 
            maxWidth: 400, 
            mx: 'auto',
            fontSize: isMobile ? '0.9rem' : '1rem'
          }}>
            You haven't joined any communities yet. Discover and join communities to connect with other members!
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/community')}
            sx={{
              background: lokiGradient,
              color: goldColor,
              fontWeight: 700,
              borderRadius: '20px',
              px: isMobile ? 3 : 4,
              py: isMobile ? 1 : 1.5,
              fontSize: isMobile ? '0.9rem' : '1rem',
              boxShadow: `0 4px 20px ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
              '&:hover': {
                boxShadow: `0 8px 30px ${alpha(isDark ? goldColor : deepGreen, 0.3)}`,
              }
            }}
          >
            Explore Communities
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={isMobile ? 2 : 3}>
          {userCommunities.map((community) => (
            <Grid 
  size={{ xs: 12, sm: 6, md: 4 }}
  key={community.id}
  sx={{ 
    display: 'flex',
    justifyContent: 'center' // Center the card in the grid item
  }}
>
              <Card 
                sx={{ 
                  borderRadius: isMobile ? '16px' : '24px', 
                  bgcolor: colors.cardBg, 
                  backdropFilter: 'blur(20px)', 
                  boxShadow: 'none', 
                  overflow: 'hidden', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  width: '100%', 
                  minWidth: '350px', 
                  maxWidth: '350px',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: isDark 
                      ? '0 20px 40px rgba(0,0,0,0.5)' 
                      : '0 20px 40px rgba(5, 24, 20, 0.1)',
                    borderColor: colors.accent
                  },
                  ...glassStyle,
                }}
                onClick={() => navigate(`/community/${community.slug}`)}
              >
                {/* Community Banner - FIXED HEIGHT */}
                <Box 
                  sx={{ 
                    height: 100, // Fixed height
                    width: '100%',
                    flexShrink: 0,
                    bgcolor: community.bannerImage ? 'transparent' : colors.accent,
                    backgroundImage: community.bannerImage 
                      ? `url(${community.bannerImage})`
                      : `linear-gradient(135deg, ${colors.accent} 0%, ${lightGreen} 100%)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative'
                  }}
                />
                
                <CardContent sx={{ 
                  pt: 0, 
                  pb: '20px !important',
                  px: 3,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  '&:last-child': { pb: '20px' }
                }}>
                  {/* Community Avatar - FIXED POSITION */}
                  <Box sx={{ 
                    display: 'flex', 
                    mb: 1, 
                    mt: -4 // Fixed -4px margin
                  }}>
                    <Avatar 
                      src={community.profilePicture}
                      sx={{ 
                        width: 64, // Fixed size
                        height: 64, // Fixed size
                        border: `1px solid ${isDark ? colors.cardBg : '#fff'}`,
                        bgcolor: colors.accent,
                        fontSize: '1.5rem', // Fixed font size
                        fontWeight: 700
                      }}
                    >
                      {community.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </Box>
                  
                  {/* Community Name - CLAMPED to 1 line */}
                  <Typography 
                    variant="h6" 
                    fontWeight={700} 
                    color={colors.textPrimary}
                    sx={{ 
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '1.1rem', // Fixed font size
                      lineHeight: 1.3,
                      mb: 0.5,
                      width: '100%',
                    }}
                  >
                    wfh/{community.slug}
                  </Typography>
                  
                  {/* Community Description - CLAMPED to 2 lines */}
                  <Typography 
                    variant="body2" 
                    color={colors.textSecondary}
                    sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: '0.85rem', // Fixed font size
                      lineHeight: 1.4,
                      mb: 2,
                      minHeight: '2.8em',
                      width: '100%',
                    }}
                  >
                    {community.description || 'No description available'}
                  </Typography>
                  
                  {/* Community Stats - FIXED HEIGHT */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    mt: 'auto',
                    flexWrap: 'wrap',
                    minHeight: '32px',
                    width: '100%',
                  }}>
                    <Tooltip title={`${community.membersCount} members`}>
                      <Typography 
                        variant="caption" 
                        color={colors.textSecondary}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          bgcolor: isDark ? alpha(colors.textSecondary, 0.1) : alpha(colors.textSecondary, 0.05),
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '12px',
                          fontSize: '0.75rem', // Fixed font size
                          minWidth: '50px',
                          flexShrink: 0
                        }}
                      >
                        <Circle sx={{ fontSize: 10, color: colors.success }} /> 
                        {community.membersCount > 999 ? 
                          `${(community.membersCount / 1000).toFixed(1)}k` : 
                          community.membersCount.toLocaleString()
                        }
                      </Typography>
                    </Tooltip>
                    
                    <Tooltip title={`${community.postsCount} posts`}>
                      <Typography 
                        variant="caption" 
                        color={colors.textSecondary}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          bgcolor: isDark ? alpha(colors.textSecondary, 0.1) : alpha(colors.textSecondary, 0.05),
                          px: 1.5,
                          py: 0.5,
                          borderRadius: '12px',
                          fontSize: '0.75rem', // Fixed font size
                          minWidth: '50px',
                          flexShrink: 0
                        }}
                      >
                        <ChatBubble sx={{ fontSize: 12 }} /> 
                        {community.postsCount > 999 ? 
                          `${(community.postsCount / 1000).toFixed(1)}k` : 
                          community.postsCount
                        }
                      </Typography>
                    </Tooltip>
                    
                    {/* Role Badge - FIXED SIZE */}
                    <Box sx={{ ml: 'auto' }}>
                      <Tooltip title={`You are a ${community.role}`}>
                        <Chip
                          label={community.role}
                          size="small"
                          sx={{
                            height: '24px', // Fixed height
                            fontSize: '0.65rem', // Fixed font size
                            fontWeight: 700,
                            bgcolor: community.role === 'owner' 
                              ? alpha('#FF6B6B', 0.2) 
                              : community.role === 'moderator'
                                ? alpha('#4CAF50', 0.2)
                                : alpha(colors.accent, 0.2),
                            color: community.role === 'owner'
                              ? '#FF6B6B'
                              : community.role === 'moderator'
                                ? '#4CAF50'
                                : colors.accent,
                            textTransform: 'capitalize',
                            '& .MuiChip-label': {
                              px: 1,
                              py: 0.5
                            }
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default CommunitiesTab;